import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import { fetchWsApprovals, WsApprovalRow } from "services/work-security-approvals.service";
import { WorkSecurityAreaScopeProps } from "services/work-security-areas.service";
import { fetchCabinetSummaryThunk, JUST_ASKED_MS } from "features/cabinet-tasks/cabinet-summary.slice";
import WsApprovalsTable from "./WsApprovalsTable";
import { PHONE_PAGE_GUTTER } from "components/tw/pageGutter";

/**
 * Tvarkos → «Laukia patvirtinimo»: pending approvals of the spheres the user
 * manages, narrowed by the module's sphere switcher (`areaId`, omitted per the
 * useAreaScope hard rule unless a concrete sphere is selected).
 */
export default function WsApprovalsTab({ areaId, areas }: WorkSecurityAreaScopeProps) {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<WsApprovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!companyId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(false);
    try {
      const data = await fetchWsApprovals(companyId, {
        status: "pending",
        ...(areaId ? { areaId } : {}),
      });
      if (requestId !== requestRef.current) return;
      setRows(data);
    } catch {
      if (requestId !== requestRef.current) return;
      setRows([]);
      setError(true);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [companyId, areaId]);

  useEffect(() => {
    load();
    // Keep the tab badge and the bell in step with what is listed.
    if (companyId) dispatch(fetchCabinetSummaryThunk(companyId, { maxAgeMs: JUST_ASKED_MS }));
  }, [load, companyId, dispatch]);

  if (!companyId) return null;

  return (
    <div className={`mt-4 space-y-3 ${PHONE_PAGE_GUTTER}`}>
      <p className="text-sm text-gray-500">{t("ws_approvals_intro")}</p>
      <WsApprovalsTable
        companyId={companyId}
        rows={rows}
        loading={loading}
        error={error}
        onRetry={load}
        showArea={!areaId && (areas?.length || 0) > 1}
      />
    </div>
  );
}
